import React, { useEffect, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import { ErrorText } from '../CustomInput/CustomInput';
import RequiredMark from '../ReqiuredMark';
import { Vector } from '../Svg/Svg';

const defaultStyles = {
    valueContainer: (provided, state) => ({
        ...provided,
        color: '#1C1C1C',
        fontSize: '14px',
        lineHeight: '22px',
        fontWeight: '400',
        backgroundColor: 'white',
        padding: '4px 8px',
    }),
    menu: (provided, state) => ({
        ...provided,
        zIndex: 2,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused
            ? '#f4f4ff'
            : state.isSelected
            ? '#e5e6ff;'
            : 'white',
        ':active': {},
        color: '#1c1c1c',
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: '400',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
    }),
    control: (provided, state) => ({
        ...provided,
        width: '100%',
        display: 'flex',
        borderColor: state.isFocused ? '#1c1c1c' : '#afafaf',
        boxShadow: 'none',
        color: '#1c1c1c',
        borderRadius: '4px',
        cursor: 'pointer',
        overflow: 'hidden',
        '&:hover': {
            // Overwrittes the different states of border
            borderColor: '#1c1c1c',
        },
    }),
    indicatorSeparator: (provided, state) => ({
        ...provided,
        display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
        transition: 'transform 100ms linear',
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#e5e6ff',
        borderRadius: '4px',
        padding: '2px 8px',
        marginRight: '4px',
        marginTop: '4px',
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: '#1c1c1c',
        fontSize: '14px',
        lineHeight: '22px',
        fontWeight: '400',
    }),
    multiValueRemove: (provided) => ({
        ...provided,
        color: '#1c1c1c',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: '#d6d7fc',
        },
    }),
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <Vector />
        </components.DropdownIndicator>
    );
};

export default function CustomMultiselect({
    labelText,
    isRequired,
    name,
    className,
    onChange,
    options,
    value,
    placeholder,
    errorText,
    customStyles,
    isLoading,
    hideSelectedOptions = true,
    closeMenuOnSelect = true,
    withAllOption = false,
    showAllTab = true,
}) {
    const [error, setError] = useState(errorText || '');

    const styles = {
        ...defaultStyles,
        ...customStyles,
    };

    useEffect(() => {
        setError(errorText);
    }, [errorText]);

    const clearError = () => {
        setError('');
    };

    const valueRef = useRef(value);
    valueRef.current = value;

    const selectAllOption = {
        value: '<SELECT_ALL>',
        label: 'All',
    };

    const isSelectAllSelected = () =>
        valueRef.current.length === options.length;

    const isOptionSelected = (option) =>
        valueRef.current.some(({ value }) => value === option.value) ||
        isSelectAllSelected();

    const getOptions = () =>
        withAllOption ? [selectAllOption, ...options] : options;

    const getValue = () =>
        isSelectAllSelected() && showAllTab ? [selectAllOption] : value;

    const onSelectChange = (newValue, actionMeta) => {
        const { action, option, removedValue } = actionMeta;

        if (
            action === 'select-option' &&
            option.value === selectAllOption.value
        ) {
            onChange(options, actionMeta);
        } else if (
            (action === 'deselect-option' &&
                option.value === selectAllOption.value) ||
            (action === 'remove-value' &&
                removedValue.value === selectAllOption.value)
        ) {
            onChange([], actionMeta);
        } else if (
            actionMeta.action === 'deselect-option' &&
            isSelectAllSelected()
        ) {
            onChange(
                options.filter(({ value }) => value !== option.value),
                actionMeta,
            );
        } else {
            onChange(newValue || [], actionMeta);
        }
    };

    return (
        <>
            {labelText && (
                <label className="input-label">
                    {labelText}
                    {isRequired && <RequiredMark />}
                </label>
            )}
            <Select
                className={error ? `${className} select__error` : className}
                classNamePrefix="select"
                name={name}
                options={getOptions()}
                styles={styles}
                onChange={onSelectChange}
                onFocus={clearError}
                value={getValue()}
                components={{ DropdownIndicator }}
                placeholder={placeholder}
                isLoading={isLoading}
                isMulti={true}
                hideSelectedOptions={hideSelectedOptions}
                closeMenuOnSelect={closeMenuOnSelect}
                isOptionSelected={isOptionSelected}
            />
            {error && <ErrorText text={error[0]} />}
        </>
    );
}
